import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { PageHeader } from "@/components/kit";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Lock, Send, Unlock, Headphones, Video, FileText, Type, Dumbbell, Plus } from "lucide-react";

const FORMATS = { audio: Headphones, video: Video, pdf: FileText, texto: Type, exercicio: Dumbbell };
const emptyC = { title: "", description: "", category: "", format: "texto", url: "", body_text: "", is_premium: false, price: 0 };

export default function TherapistContent() {
  const [list, setList] = useState([]);
  const [clients, setClients] = useState([]);
  const [cats, setCats] = useState([]);
  const [open, setOpen] = useState(false);
  const [action, setAction] = useState(null);
  const [clientId, setClientId] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [cForm, setCForm] = useState(emptyC);

  const load = () => api.get("/content").then(({ data }) => setList(data));
  useEffect(() => {
    load();
    api.get("/clients").then(({ data }) => { setClients(data); if (data[0]) setClientId(data[0].id); });
    api.get("/categories").then(({ data }) => setCats(data));
  }, []);

  const openAction = (type, content) => { setAction({ type, content }); setOpen(true); };
  const confirm = async () => {
    if (!clientId) { toast.error("Selecione um cliente"); return; }
    const url = action.type === "recommend" ? `/content/${action.content.id}/recommend` : `/content/${action.content.id}/unlock`;
    await api.post(url, { client_id: clientId });
    toast.success(action.type === "recommend" ? "Conteúdo recomendado" : "Conteúdo liberado");
    setOpen(false);
  };
  const openCreate = () => { setCForm({ ...emptyC, category: cats[0]?.name || "" }); setCreateOpen(true); };
  const saveCreate = async () => {
    if (!cForm.title || !cForm.category) { toast.error("Preencha título e categoria"); return; }
    await api.post("/content", { ...cForm, price: parseFloat(cForm.price) || 0 });
    toast.success("Conteúdo criado"); setCreateOpen(false); load();
  };

  return (
    <div>
      <PageHeader title="Biblioteca de Conteúdos" subtitle="Recomende, libere ou crie seus próprios conteúdos"
        action={<Button onClick={openCreate} className="rounded-full" data-testid="new-tcontent-btn"><Plus className="w-4 h-4 mr-1" />Criar conteúdo</Button>} />
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {list.map((c) => {
          const Icon = FORMATS[c.format] || Type;
          return (
            <Card key={c.id} className="p-5 flex flex-col" data-testid={`tcontent-${c.id}`}>
              <div className="flex items-start justify-between">
                <div className="w-10 h-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center"><Icon className="w-5 h-5" /></div>
                {c.is_premium && <Badge className="bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400"><Lock className="w-3 h-3 mr-1" />Premium</Badge>}
              </div>
              <h3 className="font-medium mt-3">{c.title}</h3>
              <p className="text-sm text-muted-foreground line-clamp-2 mt-1 flex-1">{c.description}</p>
              <Badge variant="secondary" className="w-fit mt-2">{c.category}</Badge>
              <div className="flex gap-2 mt-4">
                <Button size="sm" className="flex-1" onClick={() => openAction("recommend", c)} data-testid={`recommend-${c.id}`}><Send className="w-4 h-4 mr-1" />Recomendar</Button>
                {c.is_premium && <Button size="sm" variant="outline" onClick={() => openAction("unlock", c)} data-testid={`unlock-${c.id}`}><Unlock className="w-4 h-4" /></Button>}
              </div>
            </Card>
          );
        })}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{action?.type === "recommend" ? "Recomendar conteúdo" : "Liberar conteúdo premium"}</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">{action?.content?.title}</p>
          <div>
            <label className="text-sm font-medium">Cliente</label>
            <Select value={clientId} onValueChange={setClientId}>
              <SelectTrigger className="mt-1" data-testid="reco-client-select"><SelectValue placeholder="Selecione" /></SelectTrigger>
              <SelectContent>{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <DialogFooter><Button onClick={confirm} data-testid="confirm-reco-btn">Confirmar</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Criar conteúdo</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Título</Label><Input value={cForm.title} onChange={(e) => setCForm({ ...cForm, title: e.target.value })} data-testid="tc-title" className="mt-1" /></div>
            <div><Label>Descrição</Label><Textarea value={cForm.description} onChange={(e) => setCForm({ ...cForm, description: e.target.value })} className="mt-1" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Categoria</Label>
                <Select value={cForm.category} onValueChange={(v) => setCForm({ ...cForm, category: v })}>
                  <SelectTrigger className="mt-1" data-testid="tc-category"><SelectValue placeholder="Selecione" /></SelectTrigger>
                  <SelectContent>{cats.map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Formato</Label>
                <Select value={cForm.format} onValueChange={(v) => setCForm({ ...cForm, format: v })}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>{["audio", "video", "pdf", "texto", "exercicio"].map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div><Label>Link (áudio/vídeo/PDF)</Label><Input value={cForm.url} onChange={(e) => setCForm({ ...cForm, url: e.target.value })} className="mt-1" placeholder="https://" /></div>
            <div><Label>Texto do conteúdo</Label><Textarea value={cForm.body_text} onChange={(e) => setCForm({ ...cForm, body_text: e.target.value })} className="mt-1" rows={4} /></div>
            <div className="flex items-center gap-2"><Switch checked={cForm.is_premium} onCheckedChange={(v) => setCForm({ ...cForm, is_premium: v })} data-testid="tc-premium" /><Label>Premium (bloqueado)</Label></div>
            {cForm.is_premium && <div><Label>Preço (R$)</Label><Input type="number" value={cForm.price} onChange={(e) => setCForm({ ...cForm, price: e.target.value })} className="mt-1" /></div>}
          </div>
          <DialogFooter><Button onClick={saveCreate} data-testid="tc-save">Criar conteúdo</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
